import React, { useCallback, useEffect } from 'react'

import { CobrandingLogo } from '@frame/ui-components/cobranding-logo'
import { BaseWidgetHeader } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { P } from '@frame/ui-primitives/p'

import { DirectTaskState, DirectTaskType, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useStore } from '@frame/lib/stores/useStore'
import { getVerifierDisplayName } from '@frame/lib/utils/session'
import { isVerifiedTask } from '@frame/lib/utils/tasks'
import { DataAttribute } from '@frame/types'

import { AllTasksVerifiedContent } from './all-tasks-verified-content'
import { CompleteFooter } from './complete-footer'
import { NoTasksVerifiedContent } from './no-tasks-verified-content'
import { SomeTasksVerifiedContent } from './some-tasks-verified-content'

function getContent({
  allEmploymentTasksVerified,
  someEmploymentTasksVerified,
  verifierName,
  hasCompletedBankTask,
  showLearnMore,
}: {
  allEmploymentTasksVerified: boolean
  someEmploymentTasksVerified: boolean
  verifierName: string
  hasCompletedBankTask: boolean
  showLearnMore: boolean
}): JSX.Element {
  if (allEmploymentTasksVerified || hasCompletedBankTask) {
    return <AllTasksVerifiedContent verifierName={verifierName} showLearnMore={showLearnMore} />
  } else if (someEmploymentTasksVerified) {
    return <SomeTasksVerifiedContent verifierName={verifierName} showLearnMore={showLearnMore} />
  }

  return <NoTasksVerifiedContent verifierName={verifierName} />
}

export function Complete() {
  const { actions } = useRootContext()
  const { verifierName, tasks, consumerInitiated, verifierType, tenantPropertyName } = useStore((state) => ({
    verifierName: state.session?.originating_party_display_name ?? 'the verifier',
    tasks: state.session?.tasks ?? [],
    consumerInitiated: Boolean(state.session?.consumer_initiated),
    verifierType: state.session?.verifier_type,
    tenantPropertyName: state.session?.configuration?.cobranding?.tenant_property?.property_name,
  }))
  const completedStates = [DirectTaskState.PROCESSING, DirectTaskState.COMPLETE]
  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)

  const employmentTasks = tasks.filter((task) => task.task_type === DirectTaskType.VERIFY_EMPLOYMENT)
  const allEmploymentTasksVerified = employmentTasks.every(isVerifiedTask)
  const someEmploymentTasksVerified = employmentTasks.some(isVerifiedTask)
  const hasCompletedBankTask = tasks.some(
    (task) => task.strategy === TrueworkJsStrategy.BANK && completedStates.includes(task.state)
  )
  const hasCompletedIrsTask = tasks.some(
    (task) => task.strategy === TrueworkJsStrategy.IRS && completedStates.includes(task.state)
  )
  const hasCompletedCredentialsTask = tasks.some(
    (task) => task.strategy === TrueworkJsStrategy.CREDENTIALS && completedStates.includes(task.state)
  )

  const content = getContent({
    allEmploymentTasksVerified,
    someEmploymentTasksVerified,
    verifierName: displayName,
    hasCompletedBankTask,
    showLearnMore: hasCompletedCredentialsTask || hasCompletedBankTask,
  })

  const updateSessionState = useCallback(async () => {
    /* This ensures the stored session state is up-to-date with the backend session state once the user lands 
    on the complete screen. The updated state is needed to redirect the user to the corresponding session state 
    route if they attempt to navigate back using the browser's back button.
    */
    await actions.getSession()
  }, [actions])

  useEffect(() => {
    updateSessionState()
  }, [updateSessionState])

  return (
    <>
      <BaseWidgetHeader backButton={{ visible: false }} />
      <Body data-cy={DataAttribute.SCREEN_TERMINAL_COMPLETE} paddingTopOverride={0} className="p-5">
        <CobrandingLogo />
        <div data-cy={DataAttribute.COMPLETE_SCREEN_CONTENT} aria-live="assertive">
          {content}
        </div>
        {!consumerInitiated && <P className="mt-3">You can close this window.</P>}
      </Body>
      <CompleteFooter
        consumerInitiated={consumerInitiated}
        hasCompletedIrsTask={hasCompletedIrsTask}
        displayName={displayName}
      />
    </>
  )
}
